/**
 * Which tools drive a real browser.
 *
 * Mirrors `COMPUTER_USE_TOOL_NAMES` in `src/computer_use/registry.py`, which is the source of
 * truth; keep the two in sync when a browser-driven action is added, the same way the proto
 * copies are kept in sync.
 *
 * The client needs this to say what approving actually sets in motion. Approving one of these
 * does not just run an API call: it opens a browser the technician may be asked to sign into,
 * and which they will watch work. That is worth saying on the button rather than after it.
 */
export const COMPUTER_USE_TOOL_NAMES = new Set([
  "gworkspace_add_spam_allowlist_entries",
  "gworkspace_remove_spam_allowlist_entries",
  "gworkspace_add_spam_blocklist_entries",
  "gworkspace_remove_spam_blocklist_entries",
  "gworkspace_delete_allow_email_address_list",
  "gworkspace_delete_block_email_address_list",
  "gworkspace_delete_spam_rule",
  "gworkspace_delete_blocked_senders_rule",
  "gworkspace_get_default_routing_rule",
  "gworkspace_list_default_routing_rules",
  "gworkspace_set_default_routing_forward",
  "gworkspace_remove_default_routing_forward",
  "gworkspace_delete_default_routing_rule",
  "gworkspace_get_core_service_state",
  "gworkspace_get_auto_licensing",
  "gworkspace_set_auto_licensing",
  "gworkspace_get_user_services",
  "gworkspace_set_core_service_state",
  "gworkspace_get_spam_protection_settings",
  "gworkspace_set_spam_protection_settings",
  "gworkspace_get_password_policy",
  "gworkspace_set_password_policy",
  "gworkspace_get_2sv_policy",
  "gworkspace_set_2sv_policy",
  "gworkspace_get_login_challenge_policy",
  "gworkspace_set_login_challenge_policy",
  "gworkspace_list_routing_rules",
  "gworkspace_get_routing_rule",
  "gworkspace_create_routing_rule",
  "gworkspace_update_routing_rule",
  "gworkspace_set_routing_rule_state",
  "gworkspace_delete_routing_rule",
  "gworkspace_list_quarantines",
  "gworkspace_get_quarantine",
  "gworkspace_create_quarantine",
  "gworkspace_update_quarantine",
  "gworkspace_delete_quarantine",
  "gworkspace_initiate_user_mail_transfer",
  "gworkspace_email_log_search",
  "gworkspace_get_additional_service_settings",
  "gworkspace_set_additional_service_settings",
  "gworkspace_get_additional_services_access",
  "gworkspace_set_additional_services_access",
  "gworkspace_get_additional_service_state",
  "gworkspace_set_additional_service_state",
  "gworkspace_get_room_insights",
  "gworkspace_get_gmail_setup_settings",
  "gworkspace_set_gmail_setup_settings",
  "gworkspace_create_mail_host",
  "gworkspace_delete_mail_host",
  "gworkspace_get_account_recovery_policy",
  "gworkspace_get_calendar_general_settings",
  "gworkspace_get_calendar_sharing_settings",
  "gworkspace_get_drive_sharing_settings",
  "gworkspace_get_gmail_end_user_access",
  "gworkspace_get_gmail_safety_settings",
  "gworkspace_get_gmail_user_settings",
  "gworkspace_get_mail_host",
  "gworkspace_get_spam_rule_options",
  "gworkspace_list_drive_data_restores",
  "gworkspace_list_login_challenge_unblocks",
  "gworkspace_list_mail_hosts",
  "gworkspace_list_quarantined_messages",
  "gworkspace_list_security_recommendations",
  "gworkspace_restore_drive_data",
  "gworkspace_review_quarantined_messages",
  "gworkspace_send_sign_in_instructions",
  "gworkspace_set_account_recovery_policy",
  "gworkspace_set_calendar_general_settings",
  "gworkspace_set_calendar_sharing_settings",
  "gworkspace_set_drive_sharing_settings",
  "gworkspace_set_gmail_end_user_access",
  "gworkspace_set_gmail_safety_settings",
  "gworkspace_set_gmail_user_settings",
  "gworkspace_set_spam_rule_options",
  "gworkspace_turn_off_login_challenge",
  "gworkspace_update_mail_host",
  "gworkspace_check_user_mail_transfer",
  "slack_invite_user",
  "onepassword_add_member_to_group",
  "onepassword_batch_action",
  "onepassword_batch_read",
  "onepassword_begin_recovery",
  "onepassword_cancel_recovery",
  "onepassword_complete_recovery",
  "onepassword_confirm_member",
  "onepassword_create_vault",
  "onepassword_delete_vault",
  "onepassword_edit_member_details",
  "onepassword_edit_vault_details",
  "onepassword_get_group",
  "onepassword_get_signup_link",
  "onepassword_get_vault",
  "onepassword_grant_vault_access",
  "onepassword_invite_user",
  "onepassword_list_groups",
  "onepassword_list_invitations",
  "onepassword_list_members",
  "onepassword_list_vaults",
  "onepassword_reactivate_member",
  "onepassword_regenerate_signup_link",
  "onepassword_remove_member",
  "onepassword_remove_member_from_group",
  "onepassword_remove_pending_invitation",
  "onepassword_resend_invitation",
  "onepassword_revoke_vault_access",
  "onepassword_search_invitations",
  "onepassword_search_members",
  "onepassword_set_allowed_domains",
  "onepassword_set_vault_permission",
  "onepassword_set_vault_service_account_access",
  "onepassword_set_vault_travel_mode",
  "onepassword_suspend_member",
  "onepassword_upgrade_guest",
]);

export function isComputerUseToolName(toolName: unknown): boolean {
  return COMPUTER_USE_TOOL_NAMES.has(String(toolName || "").trim());
}

/** Would approving this human review open a browser? */
export function hitlUsesBrowser(humanReview: { tools?: Array<{ tool_name?: string }> } | null | undefined): boolean {
  return (humanReview?.tools || []).some((tool) => isComputerUseToolName(tool?.tool_name));
}
